import { useState, useCallback, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import Layout from '../../layouts/Layout';
import MaterialIssueNote from '../../components/IssueNoteHeader';
import ItemLotScanner from '../../components/ItemLotScanner';
import { apiFetch, downloadPdf } from '../../api'; // Adjust path as needed

export default function IssueNote() {
  const { id } = useParams();
  const isViewMode = Boolean(id);

  const [issueNoteData, setIssueNoteData] = useState(null);
  const [scannedItems, setScannedItems] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState({ type: '', message: '' });
  const [loading, setLoading] = useState(isViewMode);

  /* =========================
     FETCH DATA FOR VIEW MODE
     ========================= */
  useEffect(() => {
    if (!isViewMode) return;

    const fetchMaterialIssue = async () => {
      try {
        const res = await apiFetch(`/api/material-issue/view?id=${id}`);

        if (!res?.success) {
          setSaveStatus({ type: 'error', message: res?.message || 'Failed to load material issue note' });
          return;
        }

        // Populate the form with fetched data
        setIssueNoteData(res.data.issue_note);
        setScannedItems((res.data.items || []).map(item => ({
          ...item,
          // Ensure all required fields are present
          item_description: item.item_description || '',
          uom: item.uom || '',
          weight: item.weight || 0,
          qty: item.qty || 0,
          total_weight: item.total_weight || 0,
          lot_code: item.lot_code || '',
          scan_sequence: item.scan_sequence || 0,
        })));

      } catch (err) {
        console.error('Failed to load material issue note', err);
        setSaveStatus({ type: 'error', message: 'Failed to load material issue note' });
      } finally {
        setLoading(false);
      }
    };

    fetchMaterialIssue();
  }, [id, isViewMode]);

  const handleIssueNoteUpdate = useCallback((data) => {
    setIssueNoteData(data);
  }, []);

  const handleItemsUpdate = useCallback((items) => {
    setScannedItems(items);
  }, []);

  const validateData = () => {
    // Validate Material Issue Note
    if (!issueNoteData) {
      return { valid: false, message: 'Material Issue Note data is missing' };
    }

    if (!issueNoteData.min_no && !issueNoteData.autoGenerate) {
      return { valid: false, message: 'MIN No is required or enable auto-generation' };
    }

    if (!issueNoteData.date_time) {
      return { valid: false, message: 'Date/Time is required' };
    }

    if (!issueNoteData.person_issuing) {
      return { valid: false, message: 'Person Issuing is required' };
    }

    // Validate Scanned Items
    if (scannedItems.length === 0) {
      return { valid: false, message: 'No items scanned. Please scan at least one item.' };
    }
    return { valid: true, message: '' };
  };

  const handleSave = async () => {
    // Validate data
    const validation = validateData();
    if (!validation.valid) {
      setSaveStatus({ type: 'error', message: validation.message });
      setTimeout(() => setSaveStatus({ type: '', message: '' }), 5000);
      return;
    }

    setIsSaving(true);
    setSaveStatus({ type: 'info', message: 'Saving data...' });

    try {
      // Combine data from both components
      const combinedData = {
        issue_note: issueNoteData,
        items: scannedItems,
        metadata: {
          total_items: scannedItems.length,
          total_qty: scannedItems.reduce((sum, item) => sum + item.qty, 0),
          total_weight: scannedItems.reduce((sum, item) => sum + item.total_weight, 0),
          created_at: new Date().toISOString(),
        }
      };

      // If MIN No is empty, generate one
      if (!combinedData.issue_note.min_no) {
        combinedData.issue_note.min_no = `MIN-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
        combinedData.issue_note.auto_generated = true;
      }

      // Save to API
      const response = await apiFetch('/api/material-issue/save', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(combinedData),
      });

      if (response.success) {
        setSaveStatus({ 
          type: 'success', 
          message: `Material Issue Note saved successfully! MIN No: ${combinedData.issue_note.min_no}` 
        });
        
        // Reset form after successful save if needed
        setTimeout(() => {
          setSaveStatus({ type: '', message: '' });
        }, 3000);
      } else {
        throw new Error(response.message || 'Failed to save');
      }
    } catch (error) {
      console.error('Save error:', error);
      setSaveStatus({ 
        type: 'error', 
        message: `Save failed: ${error.message}` 
      });
      setTimeout(() => setSaveStatus({ type: '', message: '' }), 5000);
    } finally {
      setIsSaving(false);
    }
  };

  const handleReset = () => {
    if (window.confirm('Are you sure you want to reset all data? This action cannot be undone.')) {
      // Reset both components by triggering their internal reset
      setIssueNoteData(null);
      setScannedItems([]);
      setSaveStatus({ type: '', message: '' });
      
      // You might want to pass reset callbacks to components
      // or use refs to call their internal reset functions
    }
  };

  const handleDownloadPdf = async () => {
    if (!issueNoteData?.id) {
      setSaveStatus({ type: 'error', message: 'No Material Issue Note ID found' });
      return;
    }

    try {
      setSaveStatus({ type: 'info', message: 'Generating PDF...' });
      
      // Use the standalone PDF script
      const pdfUrl = `/pdf.php?id=${issueNoteData.id}`;
      const blob = await downloadPdf(pdfUrl);
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Material_Issue_Note_${issueNoteData.min_no}.pdf`;
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      setSaveStatus({ type: 'success', message: 'PDF downloaded successfully!' });
    } catch (error) {
      console.error('PDF download error:', error);
      setSaveStatus({ 
        type: 'error', 
        message: `PDF download failed: ${error.message}` 
      });
    }
    
    setTimeout(() => setSaveStatus({ type: '', message: '' }), 3000);
  };

  /* =========================
     LOADING STATE
     ========================= */
  if (loading) {
    return (
      <Layout>
        <h2>Loading material issue note...</h2>
      </Layout>
    );
  }

  return (
    <Layout>
      <div style={styles.container}>
        {/* Page Title */}
        <div style={styles.pageHeader}>
          <h1 style={styles.pageTitle}>
            {isViewMode ? 'View Material Issue Note' : 'Create Material Issue Note'}
          </h1>
          {isViewMode && issueNoteData?.min_no && (
            <p style={styles.pageSubtitle}>MIN No: {issueNoteData.min_no}</p>
          )}
        </div>

        {/* Save Status Banner */}
        {saveStatus.message && (
          <div style={styles.saveStatus(saveStatus.type)}>
            <div style={styles.statusIcon}>
              {saveStatus.type === 'success' ? '✓' : 
               saveStatus.type === 'error' ? '✗' : '⏳'}
            </div>
            <div style={styles.statusMessage}>{saveStatus.message}</div>
            <button 
              style={styles.statusClose}
              onClick={() => setSaveStatus({ type: '', message: '' })}
            >
              ×
            </button>
          </div>
        )}

        <MaterialIssueNote onDataUpdate={handleIssueNoteUpdate} initialData={issueNoteData} title="Material Issue Note" codePrefix="MIN" />
        <ItemLotScanner onItemsUpdate={handleItemsUpdate} initialItems={scannedItems} isViewMode={isViewMode} />
        
        {/* Combined Save Button */}
        <div style={styles.saveSection}>
          <div style={styles.summaryCard}>
            <h3 style={styles.summaryTitle}>Summary</h3>
            <div style={styles.summaryGrid}>
              <div style={styles.summaryItem}>
                <div style={styles.summaryLabel}>MIN Status:</div>
                <div style={styles.summaryValue}>
                  {issueNoteData?.min_no ? 'Ready' : 'Pending'}
                </div>
              </div>
              <div style={styles.summaryItem}>
                <div style={styles.summaryLabel}>Items Scanned:</div>
                <div style={styles.summaryValue}>{scannedItems.length}</div>
              </div>
              <div style={styles.summaryItem}>
                <div style={styles.summaryLabel}>Total Quantity:</div>
                <div style={styles.summaryValue}>
                  {scannedItems.reduce((sum, item) => sum + item.qty, 0)}
                </div>
              </div>
              <div style={styles.summaryItem}>
                <div style={styles.summaryLabel}>Total Weight:</div>
                <div style={styles.summaryValue}>
                  {scannedItems.reduce((sum, item) => sum + item.total_weight, 0).toFixed(2)} kg
                </div>
              </div>
            </div>
          </div>

          <div style={styles.actionButtons}>
            <button 
              style={styles.resetButton}
              onClick={handleReset}
              disabled={isSaving}
            >
              Reset All
            </button>
            {isViewMode && issueNoteData?.id && (
              <button 
                style={styles.pdfButton}
                onClick={handleDownloadPdf}
                disabled={isSaving}
              >
                📄 Download PDF
              </button>
            )}
            <button 
              style={styles.saveButton(isSaving)}
              onClick={handleSave}
              disabled={isSaving || scannedItems.length === 0}
            >
              {isSaving ? (
                <>
                  <span style={styles.spinner}></span>
                  {isViewMode ? 'Updating...' : 'Saving...'}
                </>
              ) : (
                isViewMode ? 'Update Material Issue Note' : 'Save Material Issue Note'
              )}
            </button>
          </div>
        </div>
      </div>
    </Layout>
  );
}

/* =========================
   STYLES
   ========================= */
const styles = {
  container: {
    maxWidth: '1400px',
    margin: '0 auto',
    padding: '16px',
    width: '100%',
  },
  
  saveStatus: (type) => ({
    marginBottom: '16px',
    padding: '12px 16px',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    backgroundColor: type === 'success' ? '#d1fae5' : 
                   type === 'error' ? '#fee2e2' : '#fef3c7',
    border: `1px solid ${type === 'success' ? '#a7f3d0' : 
                       type === 'error' ? '#fecaca' : '#fde68a'}`,
    color: type === 'success' ? '#065f46' : 
           type === 'error' ? '#991b1b' : '#92400e',
  }),
  
  statusIcon: {
    fontSize: '18px',
    fontWeight: 'bold',
  },
  
  statusMessage: {
    flex: 1,
    fontSize: '14px',
    fontWeight: '500',
  },
  
  statusClose: {
    background: 'none',
    border: 'none',
    fontSize: '20px',
    cursor: 'pointer',
    color: 'inherit',
    padding: '0',
    width: '24px',
    height: '24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  
  saveSection: {
    background: 'white',
    borderRadius: '8px',
    padding: '20px',
    marginTop: '16px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
    border: '1px solid #e5e7eb',
  },
  
  summaryCard: {
    marginBottom: '20px',
    paddingBottom: '20px',
    borderBottom: '1px solid #e5e7eb',
  },
  
  summaryTitle: {
    margin: '0 0 16px 0',
    fontSize: '16px',
    fontWeight: '600',
    color: '#1f2937',
  },
  
  summaryGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '16px',
  },
  
  summaryItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  
  summaryLabel: {
    fontSize: '13px',
    color: '#6b7280',
    fontWeight: '500',
  },
  
  summaryValue: {
    fontSize: '16px',
    color: '#1f2937',
    fontWeight: '600',
  },
  
  actionButtons: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '16px',
  },
  
  saveButton: (isLoading) => ({
    padding: '12px 32px',
    backgroundColor: isLoading ? '#93c5fd' : '#3b82f6',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: isLoading ? 'not-allowed' : 'pointer',
    transition: 'all 0.2s',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    minWidth: '200px',
    height: '44px',
    justifyContent: 'center',
    opacity: isLoading ? 0.8 : 1,
    ':hover': isLoading ? {} : {
      backgroundColor: '#2563eb',
      transform: 'translateY(-1px)',
      boxShadow: '0 4px 6px rgba(37, 99, 235, 0.2)',
    },
  }),
  
  resetButton: {
    padding: '12px 24px',
    backgroundColor: 'transparent',
    color: '#6b7280',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s',
    minWidth: '120px',
    height: '44px',
    ':hover': {
      backgroundColor: '#f3f4f6',
      color: '#374151',
    },
  },

  pdfButton: {
    padding: '12px 24px',
    backgroundColor: '#059669',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.2s',
    minWidth: '160px',
    height: '44px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    ':hover': {
      backgroundColor: '#047857',
      transform: 'translateY(-1px)',
      boxShadow: '0 4px 6px rgba(5, 150, 105, 0.2)',
    },
  },
  
  spinner: {
    width: '16px',
    height: '16px',
    border: '2px solid rgba(255,255,255,0.3)',
    borderTop: '2px solid white',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  
  '@keyframes spin': {
    '0%': { transform: 'rotate(0deg)' },
    '100%': { transform: 'rotate(360deg)' },
  },
  
  pageHeader: {
    marginBottom: '16px',
  },
  
  pageTitle: {
    margin: '0 0 8px 0',
    fontSize: '24px',
    fontWeight: '600',
    color: '#1f2937',
  },
  
  pageSubtitle: {
    margin: '0',
    fontSize: '16px',
    color: '#6b7280',
  },
};